/**
 * Per-entity-type help text: description, when to use, and sample config.
 * Keyed by "package/entity_type".
 */

export interface EntityHelp {
  /** What this entity type is. */
  description: string;
  /** When / why a designer would create one. */
  whenToUse: string;
  /** Example JSON config (rendered as code block). */
  example: Record<string, unknown>;
}

const help: Record<string, EntityHelp> = {
  // ── Resource ────────────────────────────────────────────────────────
  'resource/resource_type': {
    description:
      'A fungible resource: currency, energy, reputation, or material. Players earn and spend these across every system.',
    whenToUse:
      'Create one for every currency (gold, gems, premium tokens), energy type (stamina, action points), reputation track (faction standing), or material (wood, iron ore).',
    example: {
      category: 'currency',
      display_name: 'Gold Coins',
      max_cap: { value: 999999, decimal_places: 0 },
      decimal_places: 0,
      tradeable: true,
      tags: ['soft_currency', 'lootable'],
    },
  },
  'resource/bundle': {
    description:
      'A named group of resources granted together (e.g. "Starter Pack" = 500 gold + 50 gems).',
    whenToUse:
      'Use for shop rewards, daily login bonuses, quest completion grants, or any time you want to grant multiple resources atomically.',
    example: {
      display_name: 'Starter Pack',
      contents: {
        gold: { value: 500, decimal_places: 0 },
        gems: { value: 50, decimal_places: 0 },
      },
    },
  },
  'resource/cost': {
    description:
      'A named cost deducted from the player (e.g. "Common Upgrade" = 100 gold). Referenced by recipes, shop listings, and other systems.',
    whenToUse:
      'Create reusable cost templates that multiple systems can reference by name instead of hardcoding amounts.',
    example: {
      amounts: {
        gold: { value: 100, decimal_places: 0 },
      },
    },
  },

  // ── Inventory ───────────────────────────────────────────────────────
  'inventory/item_definition': {
    description:
      'An item type: weapon, armor, potion, material, collectible. Each instance in a player inventory references one definition.',
    whenToUse:
      'Create one for every distinct item in the game — equipment pieces, consumables, crafting materials, quest items, collectibles.',
    example: {
      category: 'weapon',
      display_name: 'Iron Sword',
      rarity: 'common',
      stackable: false,
      equippable: true,
      equipment_slot: 'main_hand',
      base_properties: { numbers: { attack: 15, durability: 100 } },
      tags: ['melee', 'sword', 'iron'],
    },
  },
  'inventory/inventory_constraints': {
    description:
      'Global inventory settings: max bag slots, weight system toggle. Usually one per game.',
    whenToUse:
      'Create once to set the default inventory size. Update when adding weight-based inventory or changing bag capacity.',
    example: {
      default_capacity: 100,
      weight_system: false,
    },
  },

  // ── Loot ────────────────────────────────────────────────────────────
  'loot/loot_table': {
    description:
      'A weighted probability table for random drops. Roll it to generate resources, items, or nothing. Supports nested tables, guaranteed drops, and pity counters.',
    whenToUse:
      'Create one for every drop source: monster kills, chest opens, quest rewards, crafting bonus outputs, daily login wheels.',
    example: {
      display_name: 'Goblin Loot',
      rolls: 1,
      guaranteed: [],
      entries: [
        { type: 'resource', resource_id: 'gold', amount: { min: { value: 5, decimal_places: 0 }, max: { value: 20, decimal_places: 0 } }, weight: 100 },
        { type: 'item', item_id: 'health_potion', amount: { value: 1, decimal_places: 0 }, weight: 30 },
        { type: 'nothing', weight: 20 },
      ],
    },
  },
  'loot/loot_modifier': {
    description:
      'A named modifier that adjusts loot odds at roll time — extra rolls, rarity shifts.',
    whenToUse:
      'Use for luck buffs, first-kill-of-day bonuses, event boosts, VIP perks.',
    example: {
      description: 'Player luck stat modifier',
      effect: { roll_count_bonus: 0, rarity_weight_shift: 0.05 },
    },
  },

  // ── Progression ─────────────────────────────────────────────────────
  'progression/progression_track': {
    description:
      'A leveling track with an XP curve: character level, crafting skill, faction reputation. Players earn XP to level up and unlock rewards.',
    whenToUse:
      'Create one for every progression axis: main character level, per-skill levels (blacksmithing, alchemy), faction reputation tiers.',
    example: {
      display_name: 'Character Level',
      max_level: 100,
      xp_curve: { type: 'polynomial', base: 100, exponent: 1.5 },
      overflow_behavior: 'bank',
      on_level_up: { skill_points: 1, stat_points: 3 },
      prestige: { enabled: true, max_prestige: 10, reset_to_level: 1, xp_multiplier: 1.1 },
    },
  },
  'progression/skill_tree': {
    description:
      'A tree of unlockable nodes with prerequisites, costs, and per-rank bonuses. Spent skill points from level-ups.',
    whenToUse:
      'Create one per class or specialization: Warrior tree, Mage tree, Crafting tree. Nodes define abilities, passives, and stat boosts.',
    example: {
      display_name: 'Warrior Skills',
      nodes: {
        heavy_strike: {
          display_name: 'Heavy Strike',
          cost: 1,
          prerequisites: [],
          max_rank: 5,
          per_rank_bonus: { damage_multiplier: 0.1 },
        },
        shield_bash: {
          display_name: 'Shield Bash',
          cost: 1,
          prerequisites: ['heavy_strike:3'],
          max_rank: 3,
        },
      },
    },
  },

  // ── Crafting ────────────────────────────────────────────────────────
  'crafting/station': {
    description: 'A crafting station (anvil, alchemy table, etc.) required by recipes.',
    whenToUse: 'Create one for each physical/virtual crafting location in the game.',
    example: {
      display_name: 'Anvil',
      required_for: ['smithing'],
    },
  },
  'crafting/recipe': {
    description:
      'A recipe: input costs (resources + items) → output rewards (items + XP). Supports success/failure, quality scaling, discovery.',
    whenToUse:
      'Create one for every craftable item or transformation. Recipes are the primary resource sink.',
    example: {
      display_name: 'Craft Iron Sword',
      category: 'smithing',
      station: 'anvil',
      skill_track: 'blacksmithing',
      min_skill_level: 1,
      resource_inputs: { gold: { value: 50, decimal_places: 0 } },
      item_inputs: { iron_ore: 5 },
      success_outputs: {
        items: [{ item_id: 'iron_sword', amount: 1, quality_scaling: true }],
        xp: { blacksmithing: 25 },
      },
      success_rate: { base: 0.8, skill_bonus: 0.02, max: 0.99 },
    },
  },

  // ── Combat ──────────────────────────────────────────────────────────
  'combat/ability': {
    description: 'An ability usable by heroes or monsters in battle — attack, heal, buff, debuff.',
    whenToUse: 'Create one for every skill, spell, or attack in the game.',
    example: {
      display_name: 'Fireball',
      damage_type: 'magical',
      power: 1.5,
      target: 'single_enemy',
      cooldown: 3,
      effects: [{ type: 'dot', duration_turns: 2, chance: 0.5 }],
    },
  },
  'combat/monster': {
    description: 'A monster type that appears in battle stages.',
    whenToUse: 'Create one for every enemy archetype — goblins, bosses, raid enemies.',
    example: {
      display_name: 'Goblin Warrior',
      element: 'nature',
      base_stats: { hp: 500, attack: 45, defense: 30, speed: 80 },
      abilities: ['slash', 'shield_bash'],
      per_level_scaling: { hp: 50, attack: 5 },
    },
  },
  'combat/stage': {
    description: 'A battle stage with waves of enemies, stamina cost, rewards, and star conditions.',
    whenToUse: 'Create one for every level, dungeon floor, or event battle.',
    example: {
      display_name: 'Dark Forest — Stage 3',
      stamina_cost: 6,
      max_heroes: 5,
      timeout_turns: 30,
      waves: [
        { enemies: [{ monster: 'goblin', count: 3, level: 5 }] },
        { enemies: [{ monster: 'goblin_chief', count: 1, level: 8 }] },
      ],
      rewards: { gold_min: 50, gold_max: 100, xp: 200, loot_table: 'forest_drop' },
    },
  },
  'combat/element_chart': {
    description: 'Element advantage/disadvantage chart (fire > nature > ice, etc.).',
    whenToUse: 'Create once per game. Defines rock-paper-scissors balance between elements.',
    example: {
      elements: {
        fire: { strong_vs: ['nature'], weak_vs: ['ice'], multiplier_strong: 1.3, multiplier_weak: 0.7 },
        ice: { strong_vs: ['fire'], weak_vs: ['nature'], multiplier_strong: 1.3, multiplier_weak: 0.7 },
        nature: { strong_vs: ['ice'], weak_vs: ['fire'], multiplier_strong: 1.3, multiplier_weak: 0.7 },
      },
    },
  },

  // ── Shop ────────────────────────────────────────────────────────────
  'shop/store': {
    description:
      'An in-game store with priced listings. Players spend currencies to buy items, bundles, or resources.',
    whenToUse:
      'Create one for each shop: general store, gem shop, daily deals, event shop. Each store has its own listings.',
    example: {
      display_name: 'General Store',
      listings: [
        {
          id: 'buy_health_potion',
          display_name: 'Health Potion',
          cost: { gold: { value: 25, decimal_places: 0 } },
          grant: { type: 'item', item_id: 'health_potion', amount: 1 },
          limit: { per_day: 0, total: 0 },
        },
        {
          id: 'daily_gem_deal',
          display_name: 'Daily Gem Deal',
          cost: { gems: { value: 10, decimal_places: 0 } },
          grant: { type: 'resources', resources: { gold: { value: 500, decimal_places: 0 } } },
          limit: { per_day: 3, total: 0 },
        },
      ],
    },
  },

  // ── Quest ───────────────────────────────────────────────────────────
  'quest/quest': {
    description:
      'A quest with objectives (kill X, collect Y, interact with Z), prerequisites, completion rewards, and optional repeatable reset.',
    whenToUse:
      'Create one for every quest chain: main story quests, side quests, daily/weekly tasks, event quests. Objectives drive player goals; rewards drive engagement.',
    example: {
      display_name: 'Goblin Menace',
      description: 'Clear the goblin camp near the village.',
      category: 'main_story',
      prerequisites: { min_level: 5, completed_quests: ['village_intro'] },
      objectives: [
        { id: 'kill_goblins', type: 'counter', target: 10, event_type: 'entity_killed', event_tag: 'goblin', display: 'Kill goblins ({{current}}/{{target}})' },
        { id: 'loot_key', type: 'collect', item_id: 'goblin_key', amount: 1, display: 'Loot the key', depends_on: ['kill_goblins'] },
      ],
      on_complete: {
        reward: { entries: [{ package: 'resource', ref_key: 'gold', amount: { value: 200, decimal_places: 0 }, metadata: {} }] },
        unlocks_quests: ['dragon_threat'],
      },
    },
  },
  // ── Achievement ─────────────────────────────────────────────────────
  'achievement/achievement': {
    description:
      'A milestone tracked passively via game events. Supports tiered progress (10 → 100 → 1000), hidden reveals, composite conditions, and configurable claim-vs-auto-grant rewards.',
    whenToUse:
      'Create one for every milestone: first kill, craft N items, reach max level, complete all quests. Use tiers for progressive goals (Goblin Hunter → Goblin Slayer). Use hidden for surprise achievements.',
    example: {
      display_name: 'Goblin Slayer',
      description: 'Eliminate goblins in increasing numbers.',
      category: 'combat',
      hidden: false,
      condition: { type: 'counter', event_type: 'entity_killed', event_tag: 'goblin', count: 100 },
      tiers: [
        { threshold: 5, display: 'Goblin Hunter', reward: { entries: [{ package: 'resource', ref_key: 'gold', amount: { value: 100, decimal_places: 0 }, metadata: {} }] } },
        { threshold: 25, display: 'Goblin Bane', reward: { entries: [{ package: 'resource', ref_key: 'gold', amount: { value: 500, decimal_places: 0 }, metadata: {} }] } },
        { threshold: 100, display: 'Goblin Slayer', reward: { entries: [{ package: 'resource', ref_key: 'gold', amount: { value: 5000, decimal_places: 0 }, metadata: {} }] } },
      ],
      reward_mode: 'claim',
    },
  },
  // ── Social ──────────────────────────────────────────────────────────
  'social/guild_template': {
    description:
      'A guild template with default settings: max members, join policy, rank structure. Used when creating new guilds from config.',
    whenToUse:
      'Create one for each guild archetype: casual guild, competitive guild, trade guild. Controls default member limits and policies.',
    example: {
      display_name: 'Standard Guild',
      max_members: 50,
      join_policy: 'open',
      ranks: ['leader', 'officer', 'member', 'recruit'],
      permissions: {
        invite: ['leader', 'officer'],
        kick: ['leader'],
        promote: ['leader'],
      },
    },
  },

  // ── Trading ────────────────────────────────────────────────────────
  'trading/marketplace_config': {
    description:
      'Marketplace settings: transaction tax, listing duration, price limits, and per-player caps. Usually one per game or one per marketplace instance.',
    whenToUse:
      'Create one for each marketplace (main market, black market, event market). Controls economy balancing levers like tax rates and listing limits.',
    example: {
      display_name: 'Main Marketplace',
      transaction_tax_percent: 5,
      listing_duration_hours: 48,
      max_active_listings: 20,
      min_price: 1,
      max_price: 1000000,
    },
  },

  // ── LiveOps ─────────────────────────────────────────────────────────
  'liveops/season': {
    description:
      'A time-bounded season with a daily login calendar and battle pass. Players earn login streaks and progress through XP-gated battle-pass tiers for free and premium rewards.',
    whenToUse:
      'Create one for every competitive/live season: Season 1, Holiday Event, etc. Each season defines its own login rewards, battle-pass tiers, and start/end dates.',
    example: {
      display_name: 'Season One',
      start_ms: 1700000000000,
      end_ms: 1710000000000,
      max_bp_tier: 50,
      xp_per_tier: 1000,
      login_calendar: [
        { day: 1, reward: { entries: [{ package: 'resource', ref_key: 'gold', amount: { value: 100, decimal_places: 0 }, metadata: {} }] } },
        { day: 7, reward: { entries: [{ package: 'resource', ref_key: 'gems', amount: { value: 10, decimal_places: 0 }, metadata: {} }] } },
      ],
      battle_pass_tiers: [
        { tier: 1, free_reward: null, premium_reward: null },
      ],
      missed_day_policy: 'reset',
    },
  },

  // ── Matchmaking ────────────────────────────────────────────────────
  'matchmaking/season_config': {
    description:
      'A competitive season with ELO rating calculations, rank tiers (Bronze/Silver/Gold), placement games, and optional rating decay and reset policies.',
    whenToUse:
      'Create one for each ranked season. Controls initial rating, K-factor, placement game count, rank tier boundaries, and what happens to ratings when the season ends.',
    example: {
      display_name: 'Season 1',
      initial_rating: 1000.0,
      k_factor: 32.0,
      placement_games: 10,
      rank_tiers: [
        { name: 'Bronze', min_rating: 0.0, icon: '' },
        { name: 'Silver', min_rating: 1200.0, icon: '' },
        { name: 'Gold', min_rating: 1500.0, icon: '' },
      ],
      decay_per_day: 0.0,
      reset_policy: { type: 'soft', compression: 0.5 },
    },
  },

  // ── Leaderboard ────────────────────────────────────────────────────
  'leaderboard/leaderboard': {
    description:
      'A leaderboard definition with sort order, reset schedule, and reward tiers distributed when the board resets.',
    whenToUse:
      'Create one for each ranking: weekly kills, daily fastest run, all-time XP. Configure reset schedule (daily/weekly/seasonal/never) and reward tiers by rank.',
    example: {
      display_name: 'Weekly Kills',
      sort: 'descending',
      reset: 'weekly',
      reward_tiers: [
        { rank_max: 1, reward: { entries: [{ package: 'resource', ref_key: 'gems', amount: { value: 100, decimal_places: 0 }, metadata: {} }] } },
        { rank_max: 10, reward: { entries: [{ package: 'resource', ref_key: 'gold', amount: { value: 500, decimal_places: 0 }, metadata: {} }] } },
      ],
    },
  },

  // ── Event ──────────────────────────────────────────────────────────
  'event/event_definition': {
    description:
      'A dynamic event with multiple phases, optional boss encounter (HP scaling by participant count), community progress tracking, contribution scoring, and tiered rewards.',
    whenToUse:
      'Create one for each world boss, community goal, or territory war. Phases drive the event lifecycle (Preparation, Active, Intermission, Resolution, Reward).',
    example: {
      display_name: 'Dragon Attack',
      phases: [
        { phase_type: 'active', duration_seconds: 3600, transition: 'manual' },
      ],
      boss_config: { base_hp: 10000, scaling_factor: 1.0 },
      community_target: 50000,
      reward_tiers: [
        { min_rank: 1, max_rank: 3, reward: { entries: [{ package: 'resource', ref_key: 'gems', amount: { value: 50, decimal_places: 0 }, metadata: {} }] } },
      ],
    },
  },

  // ── Analytics ──────────────────────────────────────────────────────
  'analytics/analytics_config': {
    description:
      'Analytics configuration defining tracked resources, anomaly detection thresholds (max grant per event, velocity limits), and engagement funnel steps.',
    whenToUse:
      'Create one per game (or per environment). Controls which resources are tracked for faucet/sink analysis and what constitutes an anomaly.',
    example: {
      display_name: 'Default',
      tracked_resources: ['gold', 'gems', 'energy'],
      anomaly_thresholds: {
        max_grant_per_event: 100000,
        velocity_window_seconds: 3600,
        velocity_max_events: 1000,
      },
    },
  },

  // ── Simulator ──────────────────────────────────────────────────────
  'simulator/sim_config': {
    description:
      'Simulation configuration defining player profiles (casual, regular, hardcore) and population mix weightings for Monte Carlo economy, progression, and loot simulations.',
    whenToUse:
      'Create one to configure how the simulator models player behavior. Define profiles with play styles, session lengths, and spending budgets. Set population mix to weight the proportion of each profile type.',
    example: {
      display_name: 'Default Simulation Config',
      default_days: 30,
      default_player_count: 1000,
      profiles: [
        { name: 'casual', display_name: 'Casual Player', sessions_per_day: 1.0, minutes_per_session: 15.0, spending_budget_usd: 0.0, play_style: 'balanced', decision_strategy: 'random', combat_skill: 0.4 },
        { name: 'hardcore', display_name: 'Hardcore Player', sessions_per_day: 4.0, minutes_per_session: 120.0, spending_budget_usd: 20.0, play_style: 'grind_focused', decision_strategy: 'optimal', combat_skill: 0.9 },
      ],
      population_mix: { profiles: { casual: 0.6, hardcore: 0.4 } },
    },
  },

  // ── Advisor ───────────────────────────────────────────────────────
  'advisor/advisor_config': {
    description:
      'Advisor configuration with game context (genre, audience, monetization model) and LLM model settings. The advisor uses this to generate tailored design recommendations.',
    whenToUse:
      'Create one per game (or per environment). Fill in the game context so the advisor can provide relevant suggestions. Choose the LLM model and set maximum suggestion count.',
    example: {
      display_name: 'Default Advisor',
      game_context: {
        title: 'My RPG',
        genre: 'RPG',
        target_audience: 'casual mobile',
        monetization: 'free-to-play',
        session_target: '10 minutes',
        retention_priority: 'D7',
        design_goals: ['balanced economy', 'rewarding progression'],
        comparable_games: ['Clash Royale', 'AFK Arena'],
      },
      max_suggestions: 5,
      model_id: 'claude-sonnet-4-20250514',
    },
  },

  // ── World ──────────────────────────────────────────────────────────
  'world/zone': {
    description:
      'A zone in the world graph: connections to other zones, entry requirements, gather nodes, biome, and death penalty rules.',
    whenToUse:
      'Create one for every distinct area: towns, dungeons, wilderness regions, instanced zones. Zones form a graph — connect them via bidirectional or one-way links.',
    example: {
      display_name: 'Dark Forest',
      biome: 'forest',
      entry_level: 5,
      connections: [
        { target_zone: 'town', bidirectional: true, travel_cost: null, cooldown_seconds: 0, required_level: 0 },
        { target_zone: 'cave', bidirectional: false, travel_cost: { gold: 10 }, cooldown_seconds: 60, required_level: 10 },
      ],
      gather_nodes: [
        { id: 'oak_tree', resource_ref: 'wood', yield_min: 1, yield_max: 5, gather_duration_seconds: 3, respawn_seconds: 30 },
      ],
      death_penalty: { gold_loss_percent: 10, respawn_zone: 'town' },
    },
  },
};

export function getEntityHelp(pkg: string, entityType: string): EntityHelp | undefined {
  return help[`${pkg}/${entityType}`];
}

export default help;
